import React, { Component } from "react";
import { RoutedViewProps } from "@my-dld/utils/UrlUtils";
import Context from "../../Context";
import Strings, { LVStrings } from "@my-dld/assets/strings";
import FormRow from "@eres/forms-web/FormRow";
import PickerInput from "@eres/forms-web/PickerInput";
import { IdExtractor } from "@my-dld/utils/LookupUtil";
import FormSection from "@my-dld/components/Form/FormSection";
import { WebFormFieldProps } from "@eres/forms-web";
import StateHolder from "@eres/forms/StateHolder";
import TextInputField from "@eres/forms-web/TextInputField";
import { AlphaSortProcessor, populateLookupOnTo, StateConsumer } from "@eres/ejari-api/Utils";
import { EjariLookUp } from "@eres/ejari-api/constants";
import FormAction from "@my-dld/components/Form/FormAction";
import { ErrorContainer } from "@eres/forms";
import { checkNonEmpty } from "@eres/ejari-web/utils";
import { LoadableDataState } from "@my-dld/utils/UIUtils";
import { getAppServices } from "@10x/coreapp";
import { RVSearchType, SearchOption } from "@10x/rentalvaluation/api/entities";
import { getParticipantID } from "@10x/sso";
import { RentalValuationApi as RVApi } from "@10x/rentalvaluation/api";
import { validateResponse } from "@10x/rentalvaluation/api/api";
import { Property as XProperty } from "@10x/rentalvaluation/api/Property";
import { resetData, updateSearchParams } from "../../Utils";
import { showProperties } from "../../NavigationUtils";
import PageTitle from "@my-dld/components/PageTitle";
import { DashboardBCItem } from "@my-dld/contexts/PageContext";
import PageContent from "@my-dld/components/PageContent";
import { Lookup } from "@eres/ejari-api/Entity";
import { SearchParams } from "../../api/entities";
import { navigateWithInParent } from "@my-dld/utils/StoreUtils";
import NumericStringConverter from "@eres/forms/converters/NumericStringConverter";

interface State extends SearchParams, LoadableDataState {
	areas?: Lookup[];
}

class Search extends Component<RoutedViewProps, State> {
	static contextType = Context;
	context!: React.ContextType<typeof Context>;

	constructor( props: RoutedViewProps ) {
		super( props );
		this.state = {
			searchBy: LAND_NO,
			loadState: "loading",
			errorContainer: {},
		};
	}

	componentDidMount() {
		populateLookupOnTo( EjariLookUp.AREAS, StateConsumer( this, "areas" ), undefined, AlphaSortProcessor );
		const { searchParams } = this.context;
		if ( searchParams ) {
			this.setState( {
				...searchParams,
			} );
		}
	}

	render() {
		const filedProps: Partial<WebFormFieldProps<any, any>> = {
			holder: StateHolder.buildStateHolder( this ),
			containerClassName: "col-md-4 form-group",
		};
		const { searchBy, areas } = this.state;
		return <PageContent title={ Strings.add_your_property } hideQLinks={ true }>
			<PageTitle title={ Strings.add_your_property } paths={ [ DashboardBCItem ] }/>
			<div className="tender-registration-form">
				<FormSection title={ Strings.search_property }>
					<div className="mandatory-field-info">
						<small>{ Strings.mandatory_fields_note }</small>
					</div>
					<FormRow { ...filedProps }>
						<PickerInput
							required={ true }
							id={ "searchBy" }
							label={ Strings.search_by }
							isLoading={ SearchByTypes === undefined }
							items={ SearchByTypes }
							toIdentifier={ IdExtractor }/>
					</FormRow>
					{ searchBy?.Id === MUNICIPALITY_NO.Id && <FormRow { ...filedProps }>
						<TextInputField
							required={ true }
							id={ "munNo" }
							label={ Strings.municipality_number }
							valueConverter={ NumericStringConverter }/>
						<TextInputField
							required={ true }
							id={ "munSubNo" }
							label={ Strings.mun_sub_number }
							valueConverter={ NumericStringConverter }/>
					</FormRow> }
					{ searchBy?.Id === LAND_NO.Id && <FormRow { ...filedProps }>
						<TextInputField
							required={ true }
							id={ "landNo" }
							label={ Strings.land_number }
							valueConverter={ NumericStringConverter }/>
						<TextInputField
							required={ true }
							id={ "landSubNo" }
							label={ Strings.mun_sub_number }
							valueConverter={ NumericStringConverter }/>
						<PickerInput
							required={ true }
							id={ "area" }
							label={ Strings.area }
							isLoading={ areas === undefined }
							items={ areas }
							toIdentifier={ IdExtractor }/>
					</FormRow> }
				</FormSection>
				<FormRow>
					<FormAction
						primaryActionLabel={ Strings.search }
						onPrimaryAction={ this.onSearch }
						secondaryActionLabel={ Strings.reset }
						onSecondaryAction={ this.onReset }
						actionContainerClassName={ "col-md-12" }/>
				</FormRow>
			</div>
		</PageContent>;
	}

	private onSearch = () => {
		if ( this.isValid() ) {
			const uiService = getAppServices().uiService;
			uiService.showLoading();
			const opt = this.getSearchOptions();
			RVApi.searchProperties( opt ).then( validateResponse ).then( apiResponse => {
				uiService.hideLoading();
				let properties: XProperty[] = apiResponse.PropertiesList ? apiResponse.PropertiesList : [];
				if ( properties.length > 0 ) {
					const property = properties[ 0 ];
					this.showProperties( property.LandPropertyId );
				} else {
					uiService.showMessage( Strings.invalid_property_id );
				}
			} ).catch( reason => {
				uiService.hideLoading();
				if ( reason && reason.type && reason.type === "ValidationError" ) {
					uiService.showMessage( reason.message );
				} else {
					uiService.showMessage( reason.toString() );
				}
			} );
		}
	};

	private isValid() {
		const { searchBy } = this.state;
		const error: ErrorContainer = {};
		let isValid = checkNonEmpty( this.state, "searchBy", error );
		if ( searchBy?.Id === MUNICIPALITY_NO.Id ) {
			isValid = checkNonEmpty( this.state, "munNo", error ) && isValid;
			isValid = checkNonEmpty( this.state, "munSubNo", error ) && isValid;
		} else if ( searchBy?.Id === LAND_NO.Id ) {
			isValid = checkNonEmpty( this.state, "landNo", error ) && isValid;
			isValid = checkNonEmpty( this.state, "landSubNo", error ) && isValid;
			isValid = checkNonEmpty( this.state, "area", error ) && isValid;
		}
		this.setState( {
			errorContainer: error,
		} );
		return isValid;
	}

	private onReset = () => {
		this.setState( {
			searchBy: undefined,
			munNo: undefined,
			munSubNo: undefined,
			landNo: undefined,
			landSubNo: undefined,
			area: undefined,
		} );
	};

	private getSearchOptions() {
		const { searchBy, munNo, munSubNo, landNo, landSubNo, area } = this.state;
		const isLandNo = ( searchBy?.Id === LAND_NO.Id );
		let opt: SearchOption = {
			participantId: getParticipantID(),
			isFreehold: 0,
		} as SearchOption;
		if ( isLandNo ) {
			opt = {
				...opt,
				searchTypeId: RVSearchType.LandNumber,
				landNo: landNo,
				landSubNo: landSubNo,
				areaId: area?.Id,
			};
		} else {
			opt = {
				...opt,
				searchTypeId: RVSearchType.DMNumber,
				dmNo: munNo,
				dmSubNo: munSubNo,
			};
		}
		return opt;
	}

	private showProperties( lPropertyId: number ) {
		resetData();
		const { areas, ...newState } = this.state;
		const searchParams: SearchParams = {
			...newState,
			landPropertyId: lPropertyId,
		};
		updateSearchParams( searchParams );
		showProperties();
		navigateWithInParent( "addproperty", "properties" );
	}
}

const LAND_NO: Lookup = {
	Id: 1,
	...LVStrings.land_number,
};
const MUNICIPALITY_NO: Lookup = {
	Id: 2, ...LVStrings.municipality_number,
};
const SearchByTypes: Lookup[] = [ LAND_NO, MUNICIPALITY_NO ];
export default Search;
